import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, IconButton, Popover, Badge,
  List, ListItem, ListItemText, Button, Divider, CircularProgress
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CampaignIcon from '@mui/icons-material/Campaign';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchCount();
    // Poll for new notifications every 30 seconds
    intervalRef.current = setInterval(fetchCount, 30000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const fetchCount = async () => {
    try {
      const { data } = await api.get('/notifications/unread-count/');
      setUnreadCount(data.unread_count);
    } catch {}
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications/');
      setNotifications(Array.isArray(data) ? data.slice(0, 20) : []);
    } catch {} finally {
      setLoading(false);
    }
  };

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => setAnchorEl(null);

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read-all/');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      await api.post(`/notifications/${notif.id}/read/`);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    }
    if (notif.notification_type === 'announcement') {
      navigate('/announcements');
    }
    handleClose();
  };

  const getIcon = (type) => {
    if (type === 'announcement') return <CampaignIcon sx={{ fontSize: 16, color: '#1a4d2e' }} />;
    return <NotificationsIcon sx={{ fontSize: 16, color: '#888' }} />;
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr)) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton onClick={handleOpen} sx={{ color: 'rgba(255,255,255,0.75)', p: 0.5 }}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 340,
            maxHeight: 480,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '0.5px solid #e0e8e0',
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2, py: 1.5,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '0.5px solid #e8ede8',
          background: '#fafcfa',
        }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            Notifications
            {unreadCount > 0 && (
              <Box component="span" sx={{
                ml: 1, px: 1, py: 0.25,
                background: '#1a4d2e', color: 'white',
                borderRadius: 10, fontSize: 11, fontWeight: 500,
              }}>
                {unreadCount}
              </Box>
            )}
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              sx={{ fontSize: 11, color: '#2d7a4f', p: 0 }}
              onClick={handleMarkAllRead}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {/* List */}
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} sx={{ color: '#1a4d2e' }} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: '#aaa' }}>No notifications yet</Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 380, overflowY: 'auto' }}>
            {notifications.map((n, i) => (
              <React.Fragment key={n.id}>
                <ListItem
                  button
                  onClick={() => handleNotificationClick(n)}
                  sx={{
                    py: 1.5, px: 2,
                    background: n.is_read ? 'transparent' : '#f0f8f0',
                    alignItems: 'flex-start',
                    cursor: 'pointer',
                    '&:hover': { background: '#f4f8f4' },
                  }}
                >
                  <Box sx={{ mr: 1.5, mt: 0.25, flexShrink: 0 }}>
                    {getIcon(n.notification_type)}
                  </Box>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: 13, fontWeight: n.is_read ? 400 : 500, lineHeight: 1.3 }}>
                        {n.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        {n.body && (
                          <Typography sx={{ fontSize: 12, color: '#888', mt: 0.25, lineHeight: 1.4 }}>
                            {n.body.slice(0, 80)}{n.body.length > 80 ? '...' : ''}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: 11, color: '#bbb', mt: 0.5 }}>
                          {timeAgo(n.created_at)}
                        </Typography>
                      </Box>
                    }
                  />
                  {!n.is_read && (
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#1a4d2e', mt: 0.75, flexShrink: 0 }} />
                  )}
                </ListItem>
                {i < notifications.length - 1 && <Divider sx={{ borderColor: '#f0f4f0' }} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}